import React, { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useAgentStore from "./useAgentStore";
import { DEFAULT_MODEL } from "../../utils/ModelConfig";
import "../Organization/OrganizationSettings.css";
import AgentConfigDrawerShell from "./agentConfig/AgentConfigDrawerShell";
import * as agentConfigDraftModel from "./agentConfig/agentConfigDraftModel";
import {
  normalizeConfiguredAgents,
  normalizeConfiguredTables,
  normalizeKnowledgeTables,
} from "./agentConfig/agentConfigDraftModel";
import {
  CREATE_PATH_AI,
  CREATE_PATH_MANUAL,
  CREATE_STAGE_CREATING_AGENT,
  CREATE_STAGE_IDLE,
  CREATE_STAGE_STARTING_CHAT,
  normalizeAiRequirementsInput,
} from "./agentConfig/agentCreation";
import {
  AgentConfigAiCreationPanel,
  AgentConfigCreationPathChooser,
} from "./agentConfig/AgentConfigCreationPanels";
import {
  DEFAULT_EMAIL_RESPONSE_POLICY_PRESET,
  buildWebhookPayloadPreview,
  ensureSendEmailEnabledForPolicy,
} from "./agentConfig/emailPolicy";
import {
  EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_UNIT,
  EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_VALUE,
  buildExternalAccessPolicy,
} from "./agentConfig/externalAccess";
import {
  KNOWLEDGE_INDEX_DEFAULT_STATUS,
  normalizeKnowledgeIndexStatus,
} from "./agentConfig/knowledgeStatus";
import {
  SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT,
  deriveSelfConfigurationModeFromEnabledTools,
  ensureSelfConfigurationToolsForMode,
  getSelfConfigurationModeHelpText,
  isSelfConfigurationEditEnabled,
} from "./agentConfig/selfConfiguration";
import {
  DEFAULT_ENABLED_TOOLS,
  INTERNAL_ONLY_TOOL_NAMES,
  SEND_SLACK_COMPONENT_TOOL_NAME,
  TABLE_SCOPE_TOOL_NAMES,
  TASK_QUEUE_UPDATE_TOOL_NAME,
  ensureTaskQueuePlannerToolsPaired,
} from "./agentConfig/toolCatalog";
import {
  buildSlackWebhookValidationError,
  extractSaveErrorMessage,
  parseSlackWebhookUrls,
} from "./agentConfig/toolConfigValidation";
import {
  buildAgentConfigChromeState,
  useAgentConfigDraft,
} from "./agentConfig/useAgentConfigDraft";
import AgentConfigTabContent from "./agentConfig/tabs/AgentConfigTabContent";
import { NAV_TABS } from "./agentConfig/navTabs";
import { useAgentConfigBootstrap } from "./agentConfig/useAgentConfigBootstrap";
import { useAgentKnowledgeActions } from "./agentConfig/useAgentKnowledgeActions";
import { useAgentConfigSave } from "./agentConfig/useAgentConfigSave";
import { usePublicShareLinkActions } from "./agentConfig/usePublicShareLinkActions";

const ACCEPTED_KNOWLEDGE_FORMATS =
  ".csv,.json,.pdf,.xlsx,.xls,.txt,.md,.docx,.pptx";
const MAX_KNOWLEDGE_FILES = 5;
export default function AgentConfigDrawer({
  presentation = "dialog",
  onPageChromeChange,
  open,
  onClose,
  agentId,
  onSaved,
  onCreated,
  initialCreationPath = null,
}) {
  const navigate = useNavigate();
  const isCreateMode = !agentId;
  const {
    isSystemAgent,
    setIsSystemAgent,
    name,
    setName,
    description,
    setDescription,
    deleteConfirmText,
    setDeleteConfirmText,
    deleting,
    setDeleting,
    systemPrompt,
    setSystemPrompt,
    model,
    setModel,
    reasoningEffort,
    setReasoningEffort,
    maxIterations,
    setMaxIterations,
    selfConfigurationMode,
    setSelfConfigurationMode,
    enabledTools,
    setEnabledTools,
    toolInstructions,
    setToolInstructions,
    sendSlackWebhookUrlsInput,
    setSendSlackWebhookUrlsInput,
    sendSlackWebhookValidationError,
    setSendSlackWebhookValidationError,
    toolCategories,
    setToolCategories,
    tableScopedToolNames,
    toolSearchQuery,
    setToolSearchQuery,
    loadingToolCatalog,
    setLoadingToolCatalog,
    runWorkflowConfigs,
    setRunWorkflowConfigs,
    workflowScopeMode,
    setWorkflowScopeMode,
    runWorkflowValidationError,
    setRunWorkflowValidationError,
    configuredTables,
    setConfiguredTables,
    configuredAgents,
    setConfiguredAgents,
    tableScopeMode,
    setTableScopeMode,
    agentScopeMode,
    setAgentScopeMode,
    fileScopeMode,
    setFileScopeMode,
    artifactScopeMode,
    setArtifactScopeMode,
    configuredTablesValidationError,
    setConfiguredTablesValidationError,
    workflowOptions,
    setWorkflowOptions,
    loadingWorkflowOptions,
    setLoadingWorkflowOptions,
    agentOptions,
    setAgentOptions,
    loadingAgentOptions,
    setLoadingAgentOptions,
    completionNotificationType,
    setCompletionNotificationType,
    completionNotifyEmails,
    setCompletionNotifyEmails,
    completionSlackWebhookUrl,
    setCompletionSlackWebhookUrl,
    completionWebhookUrl,
    setCompletionWebhookUrl,
    knowledgeRetrievalMode,
    knowledgeFiles,
    setKnowledgeFiles,
    knowledgeTables,
    setKnowledgeTables,
    knowledgeManualContext,
    setKnowledgeManualContext,
    knowledgeIndexStatus,
    setKnowledgeIndexStatus,
    inboxAddress,
    setInboxAddress,
    allowAllSenders,
    setAllowAllSenders,
    acceptEmailAttachments,
    setAcceptEmailAttachments,
    senderWhitelist,
    setSenderWhitelist,
    emailResponsePolicyPreset,
    setEmailResponsePolicyPreset,
    emailResponsePolicyCustomPrompt,
    setEmailResponsePolicyCustomPrompt,
    externalAccessPolicy,
    setExternalAccessPolicy,
    externalChatMaxSessionsPerIdentity,
    setExternalChatMaxSessionsPerIdentity,
    externalChatMaxSessionsTotal,
    setExternalChatMaxSessionsTotal,
    externalChatRateLimitMessages,
    setExternalChatRateLimitMessages,
    externalChatRateLimitPeriodValue,
    setExternalChatRateLimitPeriodValue,
    externalChatRateLimitPeriodUnit,
    setExternalChatRateLimitPeriodUnit,
    externalChatRateLimitExceededMessage,
    setExternalChatRateLimitExceededMessage,
    reviewEscalationPolicy,
    setReviewEscalationPolicy,
    blockedWhileReviewMessage,
    setBlockedWhileReviewMessage,
    customBlockedWhileReviewMessageEnabled,
    setCustomBlockedWhileReviewMessageEnabled,
    autoReplyOnBlockedIngress,
    setAutoReplyOnBlockedIngress,
    reviewTaskAssigneeUserId,
    setReviewTaskAssigneeUserId,
    reviewTaskEmailNotificationEnabled,
    setReviewTaskEmailNotificationEnabled,
    reviewTaskEligibleUsers,
    setReviewTaskEligibleUsers,
    loadingReviewTaskEligibleUsers,
    setLoadingReviewTaskEligibleUsers,
    publicShareLinkExpiresAt,
    setPublicShareLinkExpiresAt,
    enablePublicShareLinkExpiry,
    setEnablePublicShareLinkExpiry,
    showSystemPromptInChat,
    setShowSystemPromptInChat,
    showToolCallsAndOutputsInChat,
    setShowToolCallsAndOutputsInChat,
    showInterAgentMessagesInChat,
    setShowInterAgentMessagesInChat,
    showWorkflowProvenanceInChat,
    setShowWorkflowProvenanceInChat,
    showSchedulingActivityInChat,
    setShowSchedulingActivityInChat,
    showSessionOperationalMetaInChat,
    setShowSessionOperationalMetaInChat,
    showSupervisorControlMessagesInChat,
    setShowSupervisorControlMessagesInChat,
    enableHumanTakeoverMode,
    setEnableHumanTakeoverMode,
    showSharedSessionsSidebar,
    setShowSharedSessionsSidebar,
    examplePrompts,
    setExamplePrompts,
    newWhitelistEntry,
    setNewWhitelistEntry,
    databaseList,
    setDatabaseList,
    loadingDatabases,
    setLoadingDatabases,
    activeTab,
    setActiveTab,
    saving,
    setSaving,
    saveResult,
    setSaveResult,
    saveErrorMessage,
    setSaveErrorMessage,
    creationPath,
    setCreationPath,
    aiRequirements,
    setAiRequirements,
    creationStage,
    setCreationStage,
    creationError,
    setCreationError,
    loaded,
    setLoaded,
    globalVarPlaceholders,
    setGlobalVarPlaceholders,
    allAgents,
    setAllAgents,
    webhookPayloadPreview,
    isAiCreatePath,
    isManualCreatePath,
    showCreationPathChooser,
    showManualConfigurationLayout,
    normalizedAiRequirements,
    applyDraftToLocalState,
  } = useAgentConfigDraft({ isCreateMode });

  const {
    uploadingFile,
    setUploadingFile,
    indexingKnowledge,
    setIndexingKnowledge,
    deletingKnowledgeIndex,
    setDeletingKnowledgeIndex,
    handleFileDrop,
    handleFileDelete,
    handleSyncKnowledge,
    handleDeleteKnowledgeIndex,
  } = useAgentKnowledgeActions({
    agentId,
    knowledgeFiles,
    setKnowledgeFiles,
    setKnowledgeIndexStatus,
    maxKnowledgeFiles: MAX_KNOWLEDGE_FILES,
  });

  const {
    publicShareLink,
    setPublicShareLink,
    publicShareLinkError,
    setPublicShareLinkError,
    creatingPublicShareLink,
    setCreatingPublicShareLink,
    revokingPublicShareLink,
    setRevokingPublicShareLink,
    loadLatestPublicShareLink,
    handleCreatePublicShareLink,
    handleRevokePublicShareLink,
    handleCopyPublicShareLink,
  } = usePublicShareLinkActions({
    agentId,
    externalAccessPolicy,
    externalChatMaxSessionsPerIdentity,
    externalChatMaxSessionsTotal,
    externalChatRateLimitMessages,
    externalChatRateLimitPeriodValue,
    externalChatRateLimitPeriodUnit,
    externalChatRateLimitExceededMessage,
    enablePublicShareLinkExpiry,
    publicShareLinkExpiresAt,
  });

  const { persistAgentConfig } = useAgentConfigSave({
    isCreateMode,
    isAiCreatePath,
    agentId,
    onCreated,
    onSaved,
    setSaving,
    setCreationStage,
    setCreationError,
    setInboxAddress,
    setKnowledgeIndexStatus,
  });

  const applyDraftToState = useCallback(
    (draft) =>
      applyDraftToLocalState(draft, {
        setPublicShareLink,
        setPublicShareLinkError,
        setPublicShareLinkExpiresAt,
        setEnablePublicShareLinkExpiry,
      }),
    [
      applyDraftToLocalState,
      setPublicShareLink,
      setPublicShareLinkError,
      setPublicShareLinkExpiresAt,
      setEnablePublicShareLinkExpiry,
    ]
  );

  useAgentConfigBootstrap({
    open,
    agentId,
    loaded,
    applyDraftToState,
    setLoaded,
    setPublicShareLink,
    setPublicShareLinkError,
    loadLatestPublicShareLink,
    setKnowledgeIndexStatus,
    setLoadingToolCatalog,
    setToolCategories,
    setLoadingWorkflowOptions,
    setWorkflowOptions,
    setLoadingAgentOptions,
    setAgentOptions,
    setGlobalVarPlaceholders,
    setLoadingDatabases,
    setDatabaseList,
    setLoadingReviewTaskEligibleUsers,
    setReviewTaskEligibleUsers,
    setAllAgents,
  });

  useEffect(() => {
    if (!open) {
      setLoaded(false);
      applyDraftToState(agentConfigDraftModel.createEmptyAgentDraft());
      setNewWhitelistEntry("");
      setReviewTaskEligibleUsers([]);
      setLoadingReviewTaskEligibleUsers(false);
      setCreatingPublicShareLink(false);
      setRevokingPublicShareLink(false);
      setToolCategories([]);
      setToolSearchQuery("");
      setLoadingToolCatalog(false);
      setWorkflowOptions([]);
      setLoadingWorkflowOptions(false);
      setAgentOptions([]);
      setLoadingAgentOptions(false);
      setUploadingFile(false);
      setIndexingKnowledge(false);
      setDeletingKnowledgeIndex(false);
    }
  }, [open, applyDraftToState]);

  // When opened directly into a specific creation path (e.g. "Manual setup" from
  // NewAgentDialog), preselect it so the in-drawer path chooser is skipped.
  useEffect(() => {
    if (open && isCreateMode && initialCreationPath) {
      setCreationPath(initialCreationPath);
      if (initialCreationPath === CREATE_PATH_MANUAL) {
        setActiveTab("general");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isCreateMode, initialCreationPath]);

  useEffect(() => {
    const requiresConfiguredTables = enabledTools.some((toolName) =>
      tableScopedToolNames.has(toolName)
    );
    if (
      tableScopeMode === "ALL" ||
      !requiresConfiguredTables ||
      configuredTables.length > 0
    ) {
      setConfiguredTablesValidationError("");
    }
  }, [enabledTools, configuredTables, tableScopeMode]);

  useEffect(() => {
    const requiresRunWorkflow = enabledTools.includes("run_workflow");
    if (
      workflowScopeMode === "ALL" ||
      !requiresRunWorkflow ||
      runWorkflowConfigs.length > 0
    ) {
      setRunWorkflowValidationError("");
    }
  }, [enabledTools, runWorkflowConfigs, workflowScopeMode]);

  const toggleTool = (toolName) => {
    if (toolName === TASK_QUEUE_UPDATE_TOOL_NAME) {
      return;
    }
    if (
      toolName === SEND_SLACK_COMPONENT_TOOL_NAME &&
      enabledTools.includes(toolName)
    ) {
      setSendSlackWebhookValidationError("");
    }
    setEnabledTools((prev) => {
      const toggledTools = prev.includes(toolName)
        ? prev.filter((t) => t !== toolName)
        : [...prev, toolName];
      const pairedTools = ensureTaskQueuePlannerToolsPaired(toggledTools);
      const nextMode = deriveSelfConfigurationModeFromEnabledTools(pairedTools);
      const syncedTools = ensureSelfConfigurationToolsForMode(
        pairedTools,
        nextMode
      );
      setSelfConfigurationMode(nextMode);
      return syncedTools;
    });
  };

  const handleDelete = async () => {
    if (!agentId || deleteConfirmText !== name) return;
    setDeleting(true);
    const success = await useAgentStore.getState().deleteAgent(agentId);
    setDeleting(false);
    if (success) {
      onClose();
      navigate("/agents");
    }
  };

  const handleCreationPathSelect = (path) => {
    if (saving) return;
    setCreationPath(path);
    setCreationError("");
    setSaveResult(null);
    setSaveErrorMessage("");
    if (path === CREATE_PATH_MANUAL) {
      setActiveTab("general");
    }
  };

  const resetCreationPathSelection = () => {
    if (saving) return;
    setCreationPath(null);
    setCreationError("");
    setCreationStage(CREATE_STAGE_IDLE);
    setSaveResult(null);
    setSaveErrorMessage("");
  };

  const handleSave = async () => {
    if (showCreationPathChooser) return;
    if (isAiCreatePath && !normalizedAiRequirements) {
      setCreationError(
        "Add your instructions and requirements so AI can configure this agent."
      );
      setSaveResult("error");
      setSaveErrorMessage(
        "Add your instructions and requirements so AI can configure this agent."
      );
      return;
    }

    let normalizedEnabledTools = enabledTools.filter(
      (toolName) => !INTERNAL_ONLY_TOOL_NAMES.has(toolName)
    );
    normalizedEnabledTools = ensureSendEmailEnabledForPolicy(
      normalizedEnabledTools,
      emailResponsePolicyPreset
    );
    normalizedEnabledTools = ensureSelfConfigurationToolsForMode(
      normalizedEnabledTools,
      selfConfigurationMode
    );
    normalizedEnabledTools = ensureTaskQueuePlannerToolsPaired(
      normalizedEnabledTools
    );
    const normalizedSendSlackWebhookUrls = parseSlackWebhookUrls(
      sendSlackWebhookUrlsInput
    );
    const slackWebhookValidationMessage = normalizedEnabledTools.includes(
      SEND_SLACK_COMPONENT_TOOL_NAME
    )
      ? buildSlackWebhookValidationError(normalizedSendSlackWebhookUrls)
      : "";
    if (slackWebhookValidationMessage) {
      setSendSlackWebhookValidationError(slackWebhookValidationMessage);
      setRunWorkflowValidationError("");
      setSaveResult("error");
      setSaveErrorMessage(slackWebhookValidationMessage);
      setActiveTab("tools");
      return;
    }
    setSendSlackWebhookValidationError("");

    const normalizedRunWorkflowConfigs = (runWorkflowConfigs || [])
      .filter((wf) => String(wf?.workflowId || "").trim().length > 0)
      .map((wf) => {
        const workflowId = String(wf.workflowId || "").trim();
        return {
          workflowId,
          workflowName:
            String(wf.workflowName || workflowId).trim() || workflowId,
          workflowDescription: String(wf.workflowDescription || "").trim(),
          published: wf.published === true,
        };
      });
    const requiresRunWorkflowConfig =
      normalizedEnabledTools.includes("run_workflow");
    if (
      workflowScopeMode !== "ALL" &&
      requiresRunWorkflowConfig &&
      normalizedRunWorkflowConfigs.length === 0
    ) {
      const message =
        "Select at least one workflow when run_workflow is enabled.";
      setRunWorkflowValidationError(message);
      setSaveResult("error");
      setSaveErrorMessage(message);
      setActiveTab("tools");
      return;
    }
    setRunWorkflowValidationError("");

    const normalizedConfiguredTables =
      normalizeConfiguredTables(configuredTables);
    const normalizedConfiguredAgents =
      normalizeConfiguredAgents(configuredAgents);
    const normalizedKnowledgeTables = normalizeKnowledgeTables(knowledgeTables);
    const requiresConfiguredTables = normalizedEnabledTools.some((toolName) =>
      tableScopedToolNames.has(toolName)
    );
    if (
      tableScopeMode !== "ALL" &&
      requiresConfiguredTables &&
      normalizedConfiguredTables.length === 0
    ) {
      const message =
        "Select at least one table when table tools that require table IDs are enabled.";
      setConfiguredTablesValidationError(message);
      setSaveResult("error");
      setSaveErrorMessage(message);
      setActiveTab("tools");
      return;
    }
    setConfiguredTablesValidationError("");
    const normalizedReviewEscalationPolicy = reviewEscalationPolicy.trim();
    const normalizedReviewTaskAssigneeUserId = reviewTaskAssigneeUserId.trim();
    const normalizedBlockedWhileReviewMessage =
      customBlockedWhileReviewMessageEnabled
        ? blockedWhileReviewMessage.trim()
        : "";
    const effectiveEmailNotificationEnabled =
      reviewTaskEmailNotificationEnabled &&
      !!normalizedReviewTaskAssigneeUserId;
    const normalizedReviewTaskEmailNotifyUserId =
      effectiveEmailNotificationEnabled
        ? normalizedReviewTaskAssigneeUserId
        : "";
    // review_task config (escalation policy, assignee) is optional at save time —
    // the agent specifies the assignee in tool args at call time
    setSaving(true);
    setCreationError("");
    setSaveErrorMessage("");
    setSaveResult(null);
    const parsedNotifyEmails = completionNotifyEmails
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const currentDraft = {
      ...agentConfigDraftModel.createEmptyAgentDraft(),
      identity: {
        isSystemAgent,
        name,
        description,
        systemPrompt,
        model,
        reasoningEffort,
        maxIterations,
      },
      tools: {
        enabledTools,
        selfConfigurationMode,
        toolInstructions,
        sendSlackWebhookUrlsInput,
        runWorkflowConfigs,
        workflowScopeMode,
        configuredTables,
        configuredAgents,
        tableScopeMode,
        agentScopeMode,
      },
      completion: {
        notificationType: completionNotificationType,
        notifyEmails: parsedNotifyEmails.join(", "),
        slackWebhookUrl: completionSlackWebhookUrl,
        webhookUrl: completionWebhookUrl,
      },
      knowledge: {
        retrievalMode: knowledgeRetrievalMode,
        files: knowledgeFiles,
        tables: knowledgeTables,
        manualContext: knowledgeManualContext,
        indexStatus: knowledgeIndexStatus,
      },
      email: {
        inboxAddress,
        allowAllSenders,
        acceptEmailAttachments,
        senderWhitelist,
        responsePolicyPreset: emailResponsePolicyPreset,
        responsePolicyCustomPrompt: emailResponsePolicyCustomPrompt,
      },
      externalChat: {
        accessPolicy: externalAccessPolicy,
        maxSessionsPerIdentity: externalChatMaxSessionsPerIdentity,
        maxSessionsTotal: externalChatMaxSessionsTotal,
        rateLimitMessages: externalChatRateLimitMessages,
        rateLimitPeriodValue: externalChatRateLimitPeriodValue,
        rateLimitPeriodUnit: externalChatRateLimitPeriodUnit,
        rateLimitExceededMessage: externalChatRateLimitExceededMessage,
      },
      review: {
        escalationPolicy: reviewEscalationPolicy,
        blockedWhileReviewMessage,
        customBlockedWhileReviewMessageEnabled,
        autoReplyOnBlockedIngress,
        assigneeUserId: reviewTaskAssigneeUserId,
        emailNotificationEnabled: reviewTaskEmailNotificationEnabled,
      },
      examplePrompts,
      chatUi: {
        showSystemPromptInChat,
        showToolCallsAndOutputsInChat,
        showInterAgentMessagesInChat,
        showWorkflowProvenanceInChat,
        showSchedulingActivityInChat,
        showSessionOperationalMetaInChat,
        showSupervisorControlMessagesInChat,
        enableHumanTakeoverMode,
        showSharedSessionsSidebar,
      },
    };

    const data = agentConfigDraftModel.agentPayloadFromDraft(currentDraft, {
      ensureSendEmailEnabledForPolicy,
      parseSlackWebhookUrls,
    });
    const normalizedExternalAccessPolicy = data.externalAccessPolicy;
    const showResult = (result, message = "") => {
      setSaveResult(result);
      if (result === "error") {
        setSaveErrorMessage(
          message || "Save failed. Check required tool settings and try again."
        );
      } else {
        setSaveErrorMessage("");
      }
      setTimeout(() => setSaveResult(null), result === "error" ? 5000 : 2000);
    };
    const showFailureResult = (error, fallbackMessage) =>
      showResult("error", extractSaveErrorMessage(error, fallbackMessage));

    await persistAgentConfig({
      data,
      normalizedAiRequirements,
      showResult,
      showFailureResult,
    });
  };

  const renderCreationPathChooser = () => (
    <AgentConfigCreationPathChooser
      onSelectManual={() => handleCreationPathSelect(CREATE_PATH_MANUAL)}
      onSelectAi={() => handleCreationPathSelect(CREATE_PATH_AI)}
      saving={saving}
    />
  );

  const renderAiCreationPanel = () => {
    const stageLabel =
      creationStage === CREATE_STAGE_CREATING_AGENT
        ? "Creating agent..."
        : creationStage === CREATE_STAGE_STARTING_CHAT
        ? "Starting first chat..."
        : "";

    return (
      <AgentConfigAiCreationPanel
        aiRequirements={aiRequirements}
        setAiRequirements={setAiRequirements}
        creationError={creationError}
        setCreationError={setCreationError}
        saving={saving}
        stageLabel={stageLabel}
      />
    );
  };

  const tabPropsById = {
    general: {
      isSystemAgent,
      name,
      setName,
      description,
      setDescription,
      systemPrompt,
      setSystemPrompt,
      model,
      setModel,
      reasoningEffort,
      setReasoningEffort,
      maxIterations,
      setMaxIterations,
      selfConfigurationMode,
      setSelfConfigurationMode,
      setEnabledTools,
      completionNotificationType,
      setCompletionNotificationType,
      completionNotifyEmails,
      setCompletionNotifyEmails,
      completionSlackWebhookUrl,
      setCompletionSlackWebhookUrl,
      completionWebhookUrl,
      setCompletionWebhookUrl,
      globalVarPlaceholders,
      allAgents,
      webhookPayloadPreview,
      agentId,
    },
    access: {
      description,
      externalAccessPolicy,
      setExternalAccessPolicy,
      externalChatMaxSessionsPerIdentity,
      setExternalChatMaxSessionsPerIdentity,
      externalChatMaxSessionsTotal,
      setExternalChatMaxSessionsTotal,
      externalChatRateLimitMessages,
      setExternalChatRateLimitMessages,
      externalChatRateLimitPeriodValue,
      setExternalChatRateLimitPeriodValue,
      externalChatRateLimitPeriodUnit,
      setExternalChatRateLimitPeriodUnit,
      externalChatRateLimitExceededMessage,
      setExternalChatRateLimitExceededMessage,
      reviewTaskEligibleUsers,
      loadingReviewTaskEligibleUsers,
      publicShareLink,
      publicShareLinkError,
      creatingPublicShareLink,
      revokingPublicShareLink,
      publicShareLinkExpiresAt,
      setPublicShareLinkExpiresAt,
      enablePublicShareLinkExpiry,
      setEnablePublicShareLinkExpiry,
      handleCreatePublicShareLink,
      handleRevokePublicShareLink,
      handleCopyPublicShareLink,
      showSharedSessionsSidebar,
      setShowSharedSessionsSidebar,
      agentId,
    },
    chatUx: {
      isSystemAgent,
      description,
      showSystemPromptInChat,
      setShowSystemPromptInChat,
      showToolCallsAndOutputsInChat,
      setShowToolCallsAndOutputsInChat,
      showInterAgentMessagesInChat,
      setShowInterAgentMessagesInChat,
      showWorkflowProvenanceInChat,
      setShowWorkflowProvenanceInChat,
      showSchedulingActivityInChat,
      setShowSchedulingActivityInChat,
      showSessionOperationalMetaInChat,
      setShowSessionOperationalMetaInChat,
      showSupervisorControlMessagesInChat,
      setShowSupervisorControlMessagesInChat,
      enableHumanTakeoverMode,
      setEnableHumanTakeoverMode,
      examplePrompts,
      setExamplePrompts,
    },
    tools: {
      name,
      enabledTools,
      setEnabledTools,
      toggleTool,
      toolInstructions,
      setToolInstructions,
      sendSlackWebhookUrlsInput,
      setSendSlackWebhookUrlsInput,
      sendSlackWebhookValidationError,
      setSendSlackWebhookValidationError,
      toolCategories,
      tableScopedToolNames,
      toolSearchQuery,
      setToolSearchQuery,
      loadingToolCatalog,
      runWorkflowConfigs,
      setRunWorkflowConfigs,
      workflowScopeMode,
      setWorkflowScopeMode,
      runWorkflowValidationError,
      setRunWorkflowValidationError,
      configuredTables,
      setConfiguredTables,
      configuredAgents,
      setConfiguredAgents,
      tableScopeMode,
      setTableScopeMode,
      agentScopeMode,
      setAgentScopeMode,
      configuredTablesValidationError,
      setConfiguredTablesValidationError,
      workflowOptions,
      loadingWorkflowOptions,
      agentOptions,
      loadingAgentOptions,
      reviewEscalationPolicy,
      setReviewEscalationPolicy,
      blockedWhileReviewMessage,
      setBlockedWhileReviewMessage,
      customBlockedWhileReviewMessageEnabled,
      setCustomBlockedWhileReviewMessageEnabled,
      autoReplyOnBlockedIngress,
      setAutoReplyOnBlockedIngress,
      reviewTaskAssigneeUserId,
      setReviewTaskAssigneeUserId,
      reviewTaskEmailNotificationEnabled,
      setReviewTaskEmailNotificationEnabled,
      reviewTaskEligibleUsers,
      loadingReviewTaskEligibleUsers,
      databaseList,
      loadingDatabases,
      agentId,
      saving,
    },
    knowledge: {
      name,
      knowledgeFiles,
      knowledgeTables,
      setKnowledgeTables,
      knowledgeManualContext,
      setKnowledgeManualContext,
      knowledgeIndexStatus,
      uploadingFile,
      indexingKnowledge,
      deletingKnowledgeIndex,
      databaseList,
      loadingDatabases,
      handleFileDrop,
      handleFileDelete,
      handleSyncKnowledge,
      handleDeleteKnowledgeIndex,
      ACCEPTED_KNOWLEDGE_FORMATS,
      MAX_KNOWLEDGE_FILES,
      isCreateMode,
    },
    email: {
      description,
      setEnabledTools,
      inboxAddress,
      allowAllSenders,
      setAllowAllSenders,
      acceptEmailAttachments,
      setAcceptEmailAttachments,
      senderWhitelist,
      setSenderWhitelist,
      newWhitelistEntry,
      setNewWhitelistEntry,
      emailResponsePolicyPreset,
      setEmailResponsePolicyPreset,
      emailResponsePolicyCustomPrompt,
      setEmailResponsePolicyCustomPrompt,
    },
    danger: {
      name,
      deleteConfirmText,
      setDeleteConfirmText,
      deleting,
      handleDelete,
    },
  };

  const {
    isEditLoading,
    headerTitle,
    headerSubtitle,
    saveButtonDisabled,
    saveButtonLabel,
    avatarName,
  } = buildAgentConfigChromeState({
    isCreateMode,
    loaded,
    showCreationPathChooser,
    isAiCreatePath,
    name,
    saving,
    saveResult,
    normalizedAiRequirements,
    creationStage,
  });

  useEffect(() => {
    if (presentation !== "page" || !onPageChromeChange) {
      return undefined;
    }
    onPageChromeChange({
      showSave: !showCreationPathChooser && !isEditLoading,
      handleSave,
      saveButtonDisabled,
      saveButtonLabel,
      saveResult,
      saving,
      saveErrorMessage,
    });
    return () => onPageChromeChange(null);
  }, [
    presentation,
    onPageChromeChange,
    showCreationPathChooser,
    isEditLoading,
    handleSave,
    saveButtonDisabled,
    saveButtonLabel,
    saveResult,
    saving,
    saveErrorMessage,
  ]);

  return (
    <AgentConfigDrawerShell
      presentation={presentation}
      open={open}
      onClose={onClose}
      saving={saving}
      isCreateMode={isCreateMode}
      loaded={loaded}
      showCreationPathChooser={showCreationPathChooser}
      showManualConfigurationLayout={showManualConfigurationLayout}
      isEditLoading={isEditLoading}
      headerTitle={headerTitle}
      headerSubtitle={headerSubtitle}
      avatarName={avatarName}
      isSystemAgent={isSystemAgent}
      saveErrorMessage={saveErrorMessage}
      handleSave={handleSave}
      saveButtonDisabled={saveButtonDisabled}
      saveResult={saveResult}
      saveButtonLabel={saveButtonLabel}
      renderCreationPathChooser={renderCreationPathChooser}
      resetCreationPathSelection={resetCreationPathSelection}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      navTabs={NAV_TABS}
      renderAiCreationPanel={renderAiCreationPanel}
    >
      <AgentConfigTabContent
        activeTab={activeTab}
        tabPropsById={tabPropsById}
      />
    </AgentConfigDrawerShell>
  );
}
